import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { formatDateDb } from "../utils/tools";

// Xăng xe: danh sách NV, đơn đổi, lịch sử
const NUMBER_PER_PAGE = 25;
const EXPORT_LIMIT = 1000;

// Mức xăng xe (03/11/2026): 3 mức, số tiền sửa ở Cài đặt chung. BE trả `fuelLevelOptions`
// [{ level, amount, label: "Mức 2 (5.000.000đ)" }] kèm mỗi danh sách. Trống + đang đăng ký = mức 1
const FUEL_LEVELS = [1, 2, 3];

const findLevel = (level, options) =>
  FUEL_LEVELS.includes(Number(level))
    ? options?.find((o) => o.level === Number(level)) || {
        level: Number(level),
        amount: "",
        label: `Mức ${Number(level)}`,
      }
    : null;

/** Mức thực tế: null nếu không đăng ký, đăng ký mà chưa có mức => 1 */
export const effectiveFuelLevel = (registered, level) =>
  registered ? (FUEL_LEVELS.includes(Number(level)) ? Number(level) : 1) : null;

/** fuelLabel(true, 2, options) => "Đăng ký nhận xăng xe - Mức 2 (5.000.000đ)"; không truyền mức thì như cũ */
export const fuelLabel = (v, level, options) => {
  if (!v) return "Không đăng ký nhận xăng xe";
  const opt = findLevel(level, options);
  return opt ? `Đăng ký nhận xăng xe - ${opt.label}` : "Đăng ký nhận xăng xe";
};

const DEPARTMENT_LABEL = {
  0: "Khối hỗ trợ kinh doanh",
  1: "Khối kinh doanh",
  2: "Khối giám đốc",
};

const REQUEST_STATUS_LABEL = {
  PENDING: "Chưa duyệt",
  APPROVED: "Đã được duyệt",
  REJECTED: "Không được phê duyệt",
};

const HISTORY_SOURCE_LABEL = {
  REQUEST: "Duyệt đơn",
  ADMIN: "HCNS sửa trực tiếp",
};

const buildPaging = (data) => {
  const total = data?.pagination?.total || 0;
  const page = data?.pagination?.page || 1;
  const limit = data?.pagination?.limit || NUMBER_PER_PAGE;
  return {
    totalPages: Math.ceil(total / NUMBER_PER_PAGE),
    entries: total,
    startEntry: total ? (page - 1) * limit + 1 : 0,
    endEntry: Math.min(page * limit, total),
  };
};

const toQuery = (options) => {
  const clean = {};
  Object.entries(options).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") clean[k] = v;
  });
  return new URLSearchParams(clean).toString();
};

const mapEmployee = (item, options) => ({
  id: item?._id || "",
  code: item?.code || "-",
  name: item?.fullName || "-",
  // Ngày vào làm - chỉ dùng khi xuất Excel
  startDate: item?.startTime ? formatDateDb(item.startTime) : "-",
  // Khối bộ phận + Loại nhân sự + Khối / Vùng / Phòng ban tách cột (trước gộp thành "Đơn vị")
  department: DEPARTMENT_LABEL[item?.department] || "-",
  businessCategory: item?.businessCategory?.name || "-",
  saleBlock: item?.saleBlock?.name || "-",
  saleArea: item?.saleArea?.name || "-",
  supportDepartment: item?.supportDepartment?.name || "-",
  fuelRegistered: item?.fuelRegistered === true,
  fuelLevel: effectiveFuelLevel(item?.fuelRegistered === true, item?.fuelLevel),
  fuel: fuelLabel(
    item?.fuelRegistered === true,
    effectiveFuelLevel(item?.fuelRegistered === true, item?.fuelLevel),
    options,
  ),
  // Xuất Excel: mức + số tiền (trống khi không đăng ký)
  fuelLevelLabel:
    findLevel(
      effectiveFuelLevel(item?.fuelRegistered === true, item?.fuelLevel),
      options,
    )?.label || "",
  fuelAmount:
    findLevel(
      effectiveFuelLevel(item?.fuelRegistered === true, item?.fuelLevel),
      options,
    )?.amount ?? "",
  hasPendingRequest: !!item?.hasPendingRequest,
  // Khối hỗ trợ kinh doanh: luôn phải chấm ra bất kể đăng ký xăng xe
  alwaysCheckout: !!item?.alwaysCheckout,
  pending: item?.hasPendingRequest ? "Có đơn chờ duyệt" : "",
  updatedAt: item?.fuelRegisteredUpdatedAt
    ? formatDateDb(item.fuelRegisteredUpdatedAt)
    : "-",
});

/** "A → B" có mức. Đơn / lịch sử cũ không có mức: đăng ký = mức 1 */
const changeLabel = (item, options) =>
  `${fuelLabel(
    item?.fromValue,
    effectiveFuelLevel(item?.fromValue, item?.fromLevel),
    options,
  )} → ${fuelLabel(
    item?.toValue,
    effectiveFuelLevel(item?.toValue, item?.toLevel),
    options,
  )}`;

/** Bước 1 - quản lý duyệt trên app: "Không cần" / "Tên: ghi chú" */
const leaderLabel = (item) => {
  if (!item?.needLeaderStep) return "Không cần";
  const status = REQUEST_STATUS_LABEL[item?.leaderStatus] || "Chưa duyệt";
  if (!item?.leaderHandler?.fullName) return status;
  return `${status} - ${item.leaderHandler.fullName}${
    item?.leaderNote ? `: ${item.leaderNote}` : ""
  }`;
};

const mapRequest = (item, options) => ({
  id: item?._id || "",
  code: item?.employeeObject?.code || "-",
  name: item?.employeeObject?.fullName || "-",
  status: item?.status || "-",
  statusLabel: REQUEST_STATUS_LABEL[item?.status] || item?.status || "-",
  change: changeLabel(item, options),
  // Mức sau khi đổi (trống khi bỏ đăng ký) - xuất Excel
  toLevelLabel:
    findLevel(effectiveFuelLevel(item?.toValue === true, item?.toLevel), options)
      ?.label || "",
  leader: leaderLabel(item),
  toValue: item?.toValue === true,
  // Khối hỗ trợ kinh doanh (department 0) luôn phải chấm ra
  alwaysCheckout: item?.employeeObject?.department === 0,
  reason: item?.reason || "-",
  createdAt: item?.createdAt ? formatDateDb(item.createdAt) : "-",
  // Chỉ HCNS (quản lý từ chối bước 1 không ghi người xử lý)
  handler: item?.handler?.fullName
    ? `${item.handler.fullName}${item?.adminNote ? `: ${item.adminNote}` : ""}`
    : "-",
  handledAt: item?.handledAt ? formatDateDb(item.handledAt) : "-",
});

const mapHistory = (item, options) => ({
  id: item?._id || "",
  createdAt: item?.createdAt ? formatDateDb(item.createdAt) : "-",
  code: item?.employeeObject?.code || "-",
  name: item?.employeeObject?.fullName || "-",
  change: changeLabel(item, options),
  source: HISTORY_SOURCE_LABEL[item?.source] || item?.source || "-",
  creator: item?.creator?.fullName || "-",
  note: item?.note || "-",
});

async function fetchPage(path, options, mapper) {
  try {
    const data = await clientAPI(
      "get",
      `/fuelAllowance/${path}?${toQuery({ limit: NUMBER_PER_PAGE, ...options })}`,
    );
    return {
      data: (data?.items || []).map((i) => mapper(i, data?.fuelLevelOptions)),
      ...buildPaging(data),
      raw: data,
    };
  } catch (error) {
    console.error(`Error fetching fuel ${path}:`, error);
    return { data: [], totalPages: 0 };
  }
}

/**
 * Lấy hết (tối đa EXPORT_LIMIT / trang, lặp tới hết) để xuất Excel theo bộ lọc.
 * Truyền columns (exportMapping) → mỗi dòng chỉ giữ các cột có trong mapping,
 * đúng thứ tự mapping (không xuất id); cột đúng/sai ghi "Có" / "Không".
 */
export async function fetchAllFuel(path, options, columns) {
  const mapper = { employees: mapEmployee, requests: mapRequest, history: mapHistory }[path];
  let page = 1;
  const all = [];
  for (;;) {
    const data = await clientAPI(
      "get",
      `/fuelAllowance/${path}?${toQuery({ ...options, limit: EXPORT_LIMIT, page })}`,
    );
    all.push(...(data?.items || []).map((i) => mapper(i, data?.fuelLevelOptions)));
    if (!data?.pagination?.hasNextPage) break;
    page += 1;
  }
  if (!columns) return all;
  const keys = Object.keys(columns);
  const toCell = (v) => (typeof v === "boolean" ? (v ? "Có" : "Không") : v ?? "");
  return all.map((row) => Object.fromEntries(keys.map((k) => [k, toCell(row[k])])));
}

const usePagedQuery = (key, deps, fn) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [key, currentPage, ...deps],
    queryFn: () => fn(currentPage),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
  });

  return {
    items: data?.data,
    raw: data?.raw,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
};

export function useFuelEmployees(filters) {
  return usePagedQuery("fuelEmployees", [JSON.stringify(filters)], (page) =>
    fetchPage("employees", { ...filters, page }, mapEmployee),
  );
}

export function useFuelRequests(filters) {
  return usePagedQuery("fuelRequests", [JSON.stringify(filters)], (page) =>
    fetchPage("requests", { ...filters, page }, mapRequest),
  );
}

export function useFuelHistory(filters) {
  return usePagedQuery("fuelHistory", [JSON.stringify(filters)], (page) =>
    fetchPage("history", { ...filters, page }, mapHistory),
  );
}
